import { GA_FT_PARAM } from '../../constants';
import {
    DEBUG_PARAMETER,
    FT_DEBUG_PARAMETER,
    FT_PREVIEW_PARAMETER
} from '../../constants/url-params.const';

declare global {
    /** Set by hotel-site JS (e.g. Rentyl) to pass external params into Flipto */
    var ftParams: Record<string, any> | undefined;
    var ftConfigurationModel: { QueryParams?: Record<string, any> } | undefined;
}

export function rootDomain(): string {
    const parts = document.location.hostname.split('.');
    const uniqueId = '_ft-domain-detector-' + (new Date()).getTime();

    // Start checking domains from the right hand side with at least two sections i.e. x.tld
    for (var i = 2; i < parts.length; i++) {
        // Attempt to place a cookie on the constructed domain
        const domain = parts.slice(-i).join('.');
        document.cookie = uniqueId + '=1;domain=' + domain + ';';

        // Check if cookie was placed successfully
        if (document.cookie.indexOf(uniqueId + '=1') !== -1) {
            // Remove the last (and only successful) cookie
            document.cookie = uniqueId + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;domain=' + domain + ';';
            return domain;
        }
    }

    // No lower root domain available
    return document.location.hostname;
}

export function parseUrl(url: string) {
    if (!url) return {};
    const link = document.createElement('a');
    link.href = url;
    return {
        absolute: link.href,
        hash: link.hash,
        hashParams: new URLSearchParams(link.hash.split('?')[1] || link.hash.split('#')[1] || ''),
        host: link.host,
        hostname: link.hostname,
        origin: link.origin,
        original: url,
        originalOriginPath: url.split(/[\?#]/)[0],
        pathname: link.pathname,
        query: link.search,
        queryParams: new URLSearchParams(link.search)
    };
}

export function addToPath(url: string, path: string): string {
    if (url) {
        const queryChunks = url.split('?');
        const hashChunks = queryChunks[0].split('#');
        hashChunks[0] += path;
        queryChunks[0] = hashChunks.join('#');
        return queryChunks.join('?');
    }

    return undefined;
}

export function hasKey(url: string, key: string, ignoreCase = true): boolean {
    return !!key && (getValue(url, key, ignoreCase) != null);
}

export function hasEmptyKey(url: string, key: string): boolean {
    if (!key) {
        return false;
    }
    const { queryParams } = parseUrl(url);

    const hasParam = !!queryParams && Array.from((queryParams as any)?.keys())
        .some((queryKey: string) => queryKey.toLowerCase() === key.toLowerCase());


    return hasParam && !getValue(url, key);
}

export function queryParamExist(url: string, key: string): boolean {
    return hasKey(url, key) || hasEmptyKey(url, key);
}

function getCaseInsensitiveKey(urlSearchParams: URLSearchParams, key: string) {
    const lowerKey = key.toLowerCase();
    let result = undefined;
    // NOTE: Typescript doesn't see UrlSearchParams as iterable yet, but does have a forEach method
    urlSearchParams.forEach((_value, paramKey) => {
        if (paramKey.toLowerCase() === lowerKey && result == null) {
            result = paramKey;
        }
    });        
    return result != null ? result : undefined;
}

export function getValue(url: string = location.href, key: string, ignoreCase = true): string {
    if (!key || !url) {
        return undefined;
    }

    const { queryParams, hashParams } = parseUrl(url);
    
    // URLSearchParams doesn't support case-insensitive lookups
    if (ignoreCase) {
        // Case-insensitive search in query parameters
        return queryParams.get(getCaseInsensitiveKey(queryParams, key))
            ?? hashParams.get(getCaseInsensitiveKey(hashParams, key))
            ?? undefined
    }
    
    // Case-sensitive search (direct use of URLSearchParams.get())
    return queryParams.get(key)
        ?? hashParams.get(key)
        ?? undefined;
    
}

export function addToQueryString(url: string, key: string, value: string): string {
    if (!url || !key || value == null) {
        return url;
    }

    // Parse url into components
    const {originalOriginPath, queryParams, hash } = parseUrl(url);
    
    // Set the parameter using URLSearchParams
    queryParams.set(key, value);
    
    // Build new url
    return originalOriginPath + '?' + tokenAwareToString(queryParams) + hash;
}

export function addToFragment(url: string, key: string, value: string): string {
    if (!url || !key || value == null) {
        return url;
    }

    // Parse url so we can get urlsearchparams
    const { originalOriginPath, query, hash, hashParams } = parseUrl(url);

    // Set the parameter using URLSearchParams
    hashParams.set(key, value);

    // Build new url
    return originalOriginPath + query + (hash ? hash.split('?')[0] : '#') + '?' + tokenAwareToString(hashParams);
}

export function removeQueryParams(url: string): string {
    if (!url) {
        return url;
    }

    // Split URL into base part and hash part (if any)
    const hashSplit = url.split('#');
    
    // Return only the part before the query string, plus any hash fragment
    return hashSplit[0].split('?')[0] + (hashSplit.length > 1 ? '#' + hashSplit[1] : '');
}

export function removeQueryParam(url: string, param: string): string {
    if (!url || !param) {
        return url;
    }

    const { originalOriginPath, hash, queryParams } = parseUrl(url);

    // Remove the specified parameter
    queryParams.delete(getCaseInsensitiveKey(queryParams, param));

    // Construct the updated URL
    const updatedSearch = tokenAwareToString(queryParams);

    return originalOriginPath + (updatedSearch ? `?${updatedSearch}` : '') + hash;
}

export function removePathParams(url: string): string {
    if (!url) {
        return url;
    }
    const { origin, query, hash } = parseUrl(url);
    return origin + query + hash;
}


export function isDebug(url: string = location.href): boolean {
    return hasKey(url, DEBUG_PARAMETER) || hasKey(url, FT_DEBUG_PARAMETER);
}

export function isPreview(url: string = location.href): boolean {
    return queryParamExist(url, FT_PREVIEW_PARAMETER);
}

export function getAutolinkParameter(): string {
    return getValue(location.href, GA_FT_PARAM)
        || globalThis.ftConfigurationModel?.QueryParams?.[GA_FT_PARAM]
        || globalThis.ftParams?.[GA_FT_PARAM]
        || getValue(document.referrer, GA_FT_PARAM);
}

export function encodeURI(data: Object | Object[]): string | undefined {
    if (data != null) {
        return encodeURIComponent(typeof data !== 'string' ? JSON.stringify(data) : data);
    }
    return undefined;
}

export function addQueryParamWithoutParsingFullUrl(url: string, key: string, value: string){    
    if (!url || !key || value == null) {
        return url;
    }

    // Split off hash for easier query parsing
    const hashIndex = url.indexOf("#");
    let hash = "";
    if(hashIndex !== -1) {
        hash = url.substring(hashIndex);
        url = url.substring(0,hashIndex);
    }
    
    // Split off the query string
    const queryIndex = url.indexOf('?');
    let query = "";
    if(queryIndex !== -1) {
        query = url.substring(queryIndex + 1);
        url = url.substring(0, queryIndex);
    }
    
    // Create URLSearchParams object from the current query string
    const searchParams = new URLSearchParams(query);
    searchParams.set(key, value);
    
    // Build the new URL with the query string
    return url + '?' + tokenAwareToString(searchParams) + hash;
}

function tokenAwareToString(searchParams: URLSearchParams): string {
    const result: string[] = [];    
    // NOTE: Typescript doesn't see UrlSearchParams as iterable yet, but does have a forEach method
    searchParams.forEach((value, key) => {
        const open = value.indexOf('{');
        const close = value.indexOf('}')
        result.push(
            open !== -1 && close !== -1 && open < close
                ? `${key}=${value}`
                : `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)

    });
    
    return result.join('&');
}